<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    let { deviceCount, usersWithDevices, pushTypes, pushChannels } = $props();

    let title = $state('Cases Closed');
    let body = $state("Today's cases are now closed! Check your results.");
    let type = $state('results_ready');
    let userId = $state<string>('');
    let channel = $state<string>('general');
    let sending = $state(false);
    let result = $state<string | null>(null);
    let error = $state<string | null>(null);

    let targetLabel = $derived(
        userId
            ? usersWithDevices.find((u: any) => String(u.id) === userId)?.name ?? 'Unknown'
            : channel
                ? `${pushChannels.find((c: any) => c.value === channel)?.label ?? channel} channel (broadcast)`
                : `all ${deviceCount} device${deviceCount !== 1 ? 's' : ''} (individual)`
    );

    async function sendPush() {
        sending = true;
        result = null;
        error = null;

        const payload: Record<string, any> = { title, body, type };
        if (userId) {
            payload.user_id = userId;
        } else if (channel) {
            payload.channel = channel;
        }

        try {
            const response = await fetch('/admin/dev/push', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-XSRF-TOKEN': decodeURIComponent(
                        document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? ''
                    ),
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                result = data.message;
            } else if (data.errors) {
                error = Object.values(data.errors).flat().join(' ');
            } else {
                error = data.message ?? 'Unknown error';
            }
        } catch (e) {
            error = `Network error: ${e}`;
        } finally {
            sending = false;
        }
    }
</script>

<svelte:head>
    <title>Dev Console — Admin — Case of the Day</title>
</svelte:head>

<h1 class="text-2xl font-bold text-gray-100 mb-6">Dev Console</h1>

<div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-200">Send Test Push Notification</h2>
        <p class="text-sm text-gray-400">
            Sending to {targetLabel}.
        </p>

        <div>
            <label for="push-target" class="block text-sm font-medium text-gray-300 mb-1">Target</label>
            <select
                id="push-target"
                bind:value={userId}
                class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            >
                <option value="">All devices ({deviceCount})</option>
                {#each usersWithDevices as user (user.id)}
                    <option value={String(user.id)}>{user.name} ({user.email}) — {user.device_tokens_count} device{user.device_tokens_count !== 1 ? 's' : ''}</option>
                {/each}
            </select>
        </div>

        <div>
            <label for="push-channel" class="block text-sm font-medium text-gray-300 mb-1">Delivery</label>
            <select
                id="push-channel"
                bind:value={channel}
                disabled={!!userId}
                class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            >
                <option value="">Individual tokens</option>
                {#each pushChannels as ch (ch.value)}
                    <option value={ch.value}>{ch.label} channel — {ch.description}</option>
                {/each}
            </select>
        </div>

        <div>
            <label for="push-type" class="block text-sm font-medium text-gray-300 mb-1">Type</label>
            <select
                id="push-type"
                bind:value={type}
                class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            >
                {#each pushTypes as pushType (pushType.value)}
                    <option value={pushType.value}>{pushType.label} ({pushType.value})</option>
                {/each}
            </select>
            {#each pushTypes as pushType (pushType.value)}
                {#if pushType.value === type}
                    <p class="text-xs text-gray-500 mt-1">{pushType.description}</p>
                {/if}
            {/each}
        </div>

        <div>
            <label for="push-title" class="block text-sm font-medium text-gray-300 mb-1">Title</label>
            <input
                id="push-title"
                type="text"
                bind:value={title}
                class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            />
        </div>

        <div>
            <label for="push-body" class="block text-sm font-medium text-gray-300 mb-1">Body</label>
            <textarea
                id="push-body"
                bind:value={body}
                rows="3"
                class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            ></textarea>
        </div>

        <div class="flex items-center gap-3">
            <button
                onclick={sendPush}
                disabled={sending || !title || !body}
                class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
            >
                {sending ? 'Sending...' : 'Send Push'}
            </button>

            {#if result}
                <span class="text-sm text-green-400">{result}</span>
            {/if}
            {#if error}
                <span class="text-sm text-red-400">{error}</span>
            {/if}
        </div>
    </div>

    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-200">Push Notification Stats</h2>

        <div class="grid grid-cols-1 gap-4">
            <div class="flex justify-between items-center py-2 border-b border-gray-800">
                <span class="text-sm text-gray-400">Registered Devices</span>
                <span class="text-sm font-mono text-gray-100">{deviceCount}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-800">
                <span class="text-sm text-gray-400">Users with Devices</span>
                <span class="text-sm font-mono text-gray-100">{usersWithDevices.length}</span>
            </div>
        </div>

        {#if usersWithDevices.length > 0}
            <h3 class="text-sm font-medium text-gray-300 mt-4">Registered Users</h3>
            <div class="space-y-2">
                {#each usersWithDevices as user (user.id)}
                    <div class="flex justify-between items-center text-sm py-1.5 border-b border-gray-800/50">
                        <div>
                            <span class="text-gray-200">{user.name}</span>
                            <span class="text-gray-500 ml-1">{user.email}</span>
                        </div>
                        <span class="text-gray-400 font-mono text-xs">{user.device_tokens_count} device{user.device_tokens_count !== 1 ? 's' : ''}</span>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
</div>
